import React from 'react';
import HeaderImage from './HeaderImage';

/**
 * Defines the available CSS Specialized Testing profiles, along with utility functions to
 * find and merge orderableItem inputFields.
 */

/**
 * Defines a product's Selection Card structure.
 *
 * @typedef {object} ProductDefinition
 * @property {int} gdxItemId - The PICS GDX Item ID is how we tie the products to an orderable item / input field.
 * @property {JSX.Element} header - The header element, displayed above the product card.
 * @property {JSX.Element} label - The label is what is displayed next to the checkbox in the product card.
 * @property {Array<{ProductDefinition}>} components - The individually selectable components of the product.
 * @property {Array<{ProductDefinition}>} addOns - Optional add-ons of the product.
 */

const CICA = {
	name: 'CICA',
	gdxItemId: 6940,
	header: (
		<>
			<HeaderImage type="GenomicInsights" />
			<h4 className="my-2 text-center">
				CICA <small>/Celiac, IBS, & Crohn's Array</small>
			</h4>
		</>
	),
	label: (
		<strong>
			Full Array
			<br />
			(Functional Genetic + Serologic)
		</strong>
	),
	components: [
		{
			name: 'CICA Genetic',
			gdxItemId: 6941,
			label: (
				<>
					<span className="text-black bold">4 GENES:</span>
					<br />
					<span className="small">
						<i>Celiac disease</i> - HLA-DQ2.5 | HLA-DQ8
					</span>
					<br />
					<span className="small">
						<i>Crohn's disease</i> - ATG16L1: T300A | NOD2 | L1007fsinsC, R702W, G908R
					</span>
				</>
			)
		},
		{
			name: 'CICA Serological',
			gdxItemId: 6942,
			label: (
				<>
					<span className="text-black bold">ANTIBODY:</span>{' '}
					<span className="small">
						Total Serum IgA, tTG (IgG and IgA), DGP Antibody (IgG, IgA), ASCA (IgG, IgA)
					</span>
				</>
			)
		}
	]
};

const CNA = {
	name: 'Cellular Nutrition Assay',
	gdxItemId: 6943,
	header: (
		<>
			<HeaderImage type="CNA" />
			<h4 className="my-2 text-center">Cellular Nutritional Assay</h4>
		</>
	),
	label: (
		<strong>
			Full Array
			<br />
			(CNA, Redox/APA)
		</strong>
	),
	components: [
		{
			name: 'Cellular Micronutrient Assay (CMA)',
			gdxItemId: 6944,
			label: (
				<>
					<span className="text-black bold">CMA</span>{' '}
					<span className="small">(Cellular Micronutrient Assay)</span>
					<br />
					<span className="small">Micronutrient insufficiencies</span>
				</>
			)
		},
		{
			name: 'REDOX',
			gdxItemId: 6945,
			label: (
				<>
					<span className="text-black bold">Redox</span>{' '}
					<span className="small">(Overall antioxidative capticity)</span>
					<br />
					<span className="text-black bold">APA</span>{' '}
					<span className="small">(Antioxidant Protection Assay)</span>
					<br />
					<span className="small">Particulare beneficial antioxidants</span>
				</>
			)
		}
	]
};

const Histamine = {
	name: 'Histamine Intolerance',
	gdxItemId: 6948,
	header: (
		<>
			<HeaderImage type="GenomicInsights" />
			<h4 className="my-2 text-center">HISTAMINE INTOLERANCE</h4>
		</>
	),
	label: (
		<strong>
			Histamine genes
			<br />
			(Functional Genetics)
		</strong>
	)
};

const MethylDetox = {
	name: 'Methyl Detox Profile',
	gdxItemId: 6947,
	header: (
		<>
			<HeaderImage type="GenomicInsights" />
			<h4 className="my-2 text-center">METHYL DETOX PROFILE</h4>
		</>
	),
	label: <strong>5 GENES</strong>,
	components: [
		{
			name: 'COMT',
			gdxItemId: 6971,
			label: <span>COMT</span>
		},
		{
			name: 'MTRR',
			gdxItemId: 6973,
			label: <span>MTRR</span>
		},
		{
			name: 'MTR',
			gdxItemId: 6975,
			label: <span>MTR</span>
		},
		{
			name: 'MTHFR',
			gdxItemId: 6976,
			label: <span>MTHFR</span>
		},
		{
			name: 'AHCY',
			gdxItemId: 6972,
			label: <span>AHCY</span>
		}
	],
	addOns: [
		{
			name: 'Homocysteine',
			gdxItemId: 6946,
			label: <span>Homocysteine</span>
		}
	]
};

const Telomere = {
	name: 'Telomere Length',
	gdxItemId: 6949,
	header: (
		<>
			<HeaderImage type="GenomicInsights" />
			<h4 className="my-2 text-center">TELOMERE LENGTH</h4>
		</>
	),
	label: (
		<strong>
			Protective Telomere Function
			<br />
			(Functional Genetics)
		</strong>
	)
};

const ComprehensiveDelayedHypersensitivity = {
	name: 'Comprehensive Delayed Hypersensitivity Panel',
	gdxItemId: 7003,
	label: (
		<strong>
			Comprehensive Delayed Hypersensitivity Panel
		</strong>
	)
};

const ComprehensiveDelayedFoodPanel = {
	name: 'Comprehensive Delayed Food Panel',
	gdxItemId: 6999,
	label: (
		<strong>
			Comprehensive Delayed Food Panel
		</strong>
	)
};

export const ProductDefinitions = {
	CICA: CICA,
	CNA: CNA,
	Histamine: Histamine,
	MethylDetox: MethylDetox,
	Telomere: Telomere,
	ComprehensiveDelayedHypersensitivity: ComprehensiveDelayedHypersensitivity,
	ComprehensiveDelayedFoodPanel: ComprehensiveDelayedFoodPanel
};

/**
 * Builds a product object by merging a product definition with an orderable item to set input
 * field ids and filter out inactive products or components.
 */
export const buildProduct = ({ productDefinition, orderableItem }) => {
	if (!productDefinition || !orderableItem) {
		return;
	}

	const inputField = findInputFieldByGdxItemId({ orderableItem, gdxItemId: productDefinition?.gdxItemId });

	if (!inputField) {
		return;
	}

	let { components, addOns, ...product } = productDefinition;
	product.inputFieldId = inputField.id;

	product.components = components
		?.map((component) => buildProduct({ productDefinition: component, orderableItem }))
		.filter(Boolean);

	product.addOns = addOns?.map((addOn) => buildProduct({ productDefinition: addOn, orderableItem })).filter(Boolean);

	return product;
};

export const findInputFieldByGdxItemId = ({ orderableItem, gdxItemId }) => {
	if (!gdxItemId) {
		return;
	}

	return orderableItem?.inputFields?.find((field) => field.gdxItemId === gdxItemId);
};
